<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div class="flex-1 flex flex-col min-h-0 overflow-y-auto overflow-x-hidden p-4 py-0">
      <div class="flex flex-col gap-4">
        <DatePicker
          v-model="date"
          inputId="dateformat"
          dateFormat="dd-mm-yy"
          class="calendar w-full"
          :minDate="today"
          :pt="{
            root: {class: '!w-full'},
            panel: {class: '!border-0 !p-0 !w-full !min-w-0'},
            header: {class: '!border-0 !pb-0'}
          }"
          inline />

        <!-- Visit length — defaults to the chosen services, adjustable per visit.
             Same stepper the scheduling settings use, so the control reads the same
             wherever a number is being nudged. -->
        <div class="rounded-xl border border-gray-200 overflow-hidden">
          <div class="flex items-center gap-3 px-3 py-2.5">
            <span class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-none">
              <i class="pi pi-hourglass text-sm" />
            </span>
            <div class="flex-1 min-w-0">
              <div class="text-[13px] font-medium">משך הביקור</div>
              <div class="text-[11px] text-gray-500 truncate">
                {{ isDurationOverridden ? `שונה ידנית · ברירת מחדל ${defaultDuration} דק׳` : 'לפי השירותים שנבחרו' }}
              </div>
            </div>
            <settings-stepper
              :modelValue="effectiveDuration"
              :min="MIN_DURATION"
              :max="MAX_DURATION"
              :step="DURATION_STEP"
              suffix="דק׳"
              @update:modelValue="setDuration"
            />
          </div>

          <!-- Jumps to the common lengths, so a long visit isn't 20 taps away -->
          <div class="flex items-center gap-1.5 flex-wrap px-3 pb-2.5 pt-2 border-t border-gray-100">
            <button
              v-for="preset in DURATION_PRESETS"
              :key="preset"
              type="button"
              class="px-2.5 py-1 rounded-full border text-xs transition-colors"
              :class="effectiveDuration === preset ? 'border-primary text-primary bg-primary-50' : 'border-gray-200 text-gray-600'"
              @click="setDuration(preset)"
            >{{ preset }} דק׳</button>
            <button
              v-if="isDurationOverridden"
              type="button"
              class="text-xs text-primary underline underline-offset-2 ms-auto"
              @click="resetDuration"
            >איפוס</button>
          </div>
        </div>

        <!-- Time Slot Selection -->
        <div class="flex flex-col gap-2 mb-6">
          <span class="font-medium">שעת התור</span>
          <settings-toggle
            class="!px-2"
            :status="showOutOfWorkingHours"
            title="הצגת שעות מחוץ לשעות הפעילות"
            @value-changed="(val) => {showOutOfWorkingHours = !showOutOfWorkingHours} "/>

          <div v-if="availableSlots.length"
               class="flex flex-wrap w-full gap-2 mt-2 justify-start items-start">
            <div v-for="slot in availableSlots.filter(x => showOutOfWorkingHours ? true : !x.name.includes('- admin'))"
                 @click="selectHour(slot)"
                 :class="[
                   'rounded-lg py-2 px-3 border text-center w-[calc(100%/4-6px)] md:cursor-pointer',  (hour?.value === slot.value ? 'border-black border-2' : '')]">
              <span>{{ slot.name.replace('- admin', '') }}</span>
            </div>
            <span v-if="availableSlots.filter(x => showOutOfWorkingHours ? true : !x.name.includes('- admin')).length === 0">אין תורים פנויים</span>
          </div>
          <div v-else-if="!availableSlotsWasLoaded"
               class="flex flex-wrap w-full gap-2 justify-start items-start">
            <div v-for="i in [1,2,3,4,5,6]">
              <Skeleton borderRadius="8px" width="5.5rem" height="2.5rem" />
            </div>
          </div>
          <span v-else>אין תורים פנויים</span>

          <!-- Custom hour -->
          <div class="flex flex-col gap-2 mt-4 pt-4 border-t">
            <span class="text-sm font-medium">שעה מותאמת אישית</span>
            <div class="flex gap-2 items-center">
              <InputText
                v-model="customHourInput"
                size="small"
                class="flex-1"
                placeholder="לדוגמה 08:43"
                maxlength="5"
                @keyup.enter="applyCustomHour" />
              <Button label="הוספה" size="small" @click="applyCustomHour" />
            </div>
            <small v-if="customHourError" class="text-red-500">{{ customHourError }}</small>
            <!-- A typed time is booked exactly as entered — the same override the edit
                 flow has, so the owner can double-book or work outside their hours. -->
            <div
              v-if="hourWasTyped"
              class="flex items-start gap-2 rounded-lg bg-primary-50 border border-primary-100 px-3 py-2">
              <i class="pi pi-bolt text-primary text-sm mt-0.5" />
              <span class="text-xs text-primary-700">
                השעה {{ hour?.value }} תיקבע בדיוק כפי שהוזנה — גם אם היא חופפת לתור אחר או מחוץ לשעות הפעילות.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="w-full px-4 flex-shrink-0 shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.05)]">
      <drawer-footer
        approve-label="בחירת תאריך"
        @approve-click="chooseDate"
        @cancel-click="emit('cancel')"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, PropType, ref, watch } from 'vue';
import { getStringHour, totalServicesDuration } from '@torim-app/utils';
import { DateTime } from 'luxon';
import DatePicker from 'primevue/datepicker';
import SettingsStepper from '@app/components/admin/pages/configurations/ui/SettingsStepper.vue';
import { IDropdownItem, IService } from '@torim-app/types';
import { CustomersApi } from '@app/api/admin';
import { useDrawer } from '@torim-app/shared-components';

const props = defineProps({
  selectedServices: {
    required: true,
    type: Array as PropType<IService[]>,
    default: () => []
  },
  selectedProvider: {
    required: true,
    type: String,
    default: ''
  },
  preDate: {
    required: false,
    type: Date,
    default: undefined
  },
  initialDate: {
    required: false,
    type: Date,
    default: undefined
  },
  initialHour: {
    required: false,
    type: Object as PropType<IDropdownItem>,
    default: undefined
  },
  /**
   * Visit length (minutes) the owner already set for this appointment, if any.
   * Undefined => the chosen services decide.
   */
  initialDuration: {
    required: false,
    type: Number,
    default: undefined
  }
});

const emit = defineEmits<{
  dateSelected: [date: Date]
  hourSelected: [hour: IDropdownItem]
  /** Minutes, or undefined when the owner reset back to the services' own length. */
  durationChanged: [duration: number | undefined]
  /** True once the hour was typed by hand, so the booking is forced through as-is. */
  forcedHourChanged: [forced: boolean]
  cancel: []
}>();

const { closeDrawer } = useDrawer();

/** Common visit lengths, offered as one-tap shortcuts beside the stepper. */
const DURATION_PRESETS = [15, 30, 45, 60, 90, 120];
const MIN_DURATION = 5;
const MAX_DURATION = 600;
const DURATION_STEP = 5;
/** Settle time before reloading the slots, so holding the stepper doesn't refetch per tap. */
const DURATION_RELOAD_DEBOUNCE_MS = 400;

const date = ref<Date | undefined>(props.initialDate);
const availableSlots = ref<IDropdownItem[]>([]);
const availableSlotsWasLoaded = ref(false);
const hour = ref<IDropdownItem | undefined>(props.initialHour);
const showOutOfWorkingHours = ref<boolean>(false);
const today = new Date();

// ── Visit length ──
// The services' own total is the default; `durationOverride` holds the owner's choice
// for this one visit. Slots are always fetched for whichever is in effect.
const defaultDuration = computed(() => totalServicesDuration(props.selectedServices, props.selectedProvider));
const durationOverride = ref<number | undefined>(props.initialDuration);
const effectiveDuration = computed(() => durationOverride.value ?? defaultDuration.value);
const isDurationOverridden = computed(() =>
  durationOverride.value !== undefined && durationOverride.value !== defaultDuration.value
);
let durationReloadTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Apply a length straight away — the stepper edits live, like the settings screens do.
 * Only the slot reload waits for the taps to stop.
 */
const commitDuration = (minutes: number | undefined) => {
  durationOverride.value = minutes;
  emit('durationChanged', minutes);
  // The grid depends on the length, so a previously picked slot may no longer be
  // bookable. Typed times are the owner's explicit override and are left alone.
  if (!hourWasTyped.value) hour.value = undefined;

  if (durationReloadTimer) clearTimeout(durationReloadTimer);
  durationReloadTimer = setTimeout(() => fetchAvailableSlots(), DURATION_RELOAD_DEBOUNCE_MS);
};

const setDuration = (minutes: number) => {
  const clamped = Math.min(MAX_DURATION, Math.max(MIN_DURATION, minutes));
  // Landing back on the services' own total clears the override rather than pinning
  // the same number, so later service changes keep flowing through.
  commitDuration(clamped === defaultDuration.value ? undefined : clamped);
};

const resetDuration = () => commitDuration(undefined);

onBeforeUnmount(() => {
  if (durationReloadTimer) clearTimeout(durationReloadTimer);
});

const customHourInput = ref<string>('');
const customHourError = ref<string>('');
// Set when the owner typed a time by hand. Tracked explicitly (rather than inferred from
// the slot list) so reloading the slots can't silently reclassify a normal pick.
const hourWasTyped = ref(false);
const selectHour = (slot: IDropdownItem) => {
  hour.value = slot;
  hourWasTyped.value = false;
  emit('forcedHourChanged', false);
  customHourError.value = '';
};

const padTime = (num: number) => (num < 10 ? `0${num}` : `${num}`);

const applyCustomHour = () => {
  customHourError.value = '';
  const match = customHourInput.value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    customHourError.value = 'יש להזין שעה בפורמט HH:MM (לדוגמה 08:43)';
    return;
  }

  const hours = +match[1];
  const minutes = +match[2];
  if (hours > 23 || minutes > 59) {
    customHourError.value = 'שעה לא תקינה';
    return;
  }

  const numericValue = hours + minutes / 60;
  const stringHour = `${padTime(hours)}:${padTime(minutes)}`;
  // Apply the typed time directly. A custom time may overlap an existing
  // appointment or fall outside working hours, but the owner explicitly chose
  // it — schedule it without an extra confirmation step.
  hour.value = { name: stringHour, value: stringHour, numericValue };
  hourWasTyped.value = true;
  emit('forcedHourChanged', true);
};

const fetchAvailableSlots = async () => {
  let usedDate = date.value ?? props.preDate ?? today;
  availableSlots.value.length = 0;
  availableSlotsWasLoaded.value = false;
  const serviceIds = props.selectedServices.map(x => x.id).join(',');
  const selectedDate = DateTime.fromJSDate(usedDate).toISODate()!;
  const slots = await CustomersApi.fetchSlotsByDateAndService(
    serviceIds, selectedDate, props.selectedProvider, durationOverride.value
  );

  availableSlots.value.push(...[
    ...slots.available.map(x => ({
      name: getStringHour(x),
      value: getStringHour(x),
      numericValue: x
    })),
    ...slots.adminAvailable.map(x => ({
      name: `${getStringHour(x)} - admin`,
      value: getStringHour(x),
      numericValue: x
    }))].sort((a, b) => a.numericValue > b.numericValue ? 1 : -1));

  if (props.preDate && !hour.value) {
    const matchedHour = availableSlots.value.find(x => Math.floor(x.numericValue!) === props.preDate!.getHours());
    if (matchedHour)
      hour.value = matchedHour;
  }

  availableSlotsWasLoaded.value = true;
};

const chooseDate = () => {
  emit('dateSelected', date.value ?? props.preDate ?? today);
  if (hour.value) {
    emit('hourSelected', hour.value);
  }
  closeDrawer();
};

watch(() => props.selectedProvider, () => {
  if (props.selectedProvider && props.selectedServices && props.selectedServices.length > 0)
    fetchAvailableSlots();
});

watch(() => props.selectedServices, () => {
  if (props.selectedProvider && props.selectedServices && props.selectedServices.length > 0)
    fetchAvailableSlots();
});

watch(date, () => fetchAvailableSlots());

// Fetch on mount if we have the required data
if (props.selectedProvider && props.selectedServices && props.selectedServices.length > 0) {
  fetchAvailableSlots();
}
</script>

<style lang="scss" scoped>
/*
 * PrimeVue sizes each day cell at a fixed 2.5rem plus padding, so the grid needs ~360px.
 * Inside this drawer (minus its own padding) that overflows on a narrow phone: the panel
 * spilled past the right edge and the container scrolled horizontally, hiding the
 * next-month arrow until the user happened to swipe. Let the table fill the available
 * width and the cells divide it evenly instead, capped at the original size so it doesn't
 * stretch on wide screens.
 */
:deep(.p-datepicker-day-view) {
  width: 100%;
  table-layout: fixed;
}

:deep(.p-datepicker-day-cell),
:deep(.p-datepicker-weekday-cell) {
  padding: 0.125rem;
}

:deep(.p-datepicker-day),
:deep(.p-datepicker-weekday) {
  width: 100%;
  max-width: 2.5rem;
  margin-inline: auto;
}
</style>
